import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    setResultsOpen as setResultsOpenAction,
    setReportLevelWithColumns,
    setReportTypeWithColumns,
    setConflictLevelWithColumns,
    setSelectedIds as setSelectedIdsAction,
    setSearchString as setSearchStringAction,
    setPageNumber as setPageNumberAction,
    setLimitNumber as setLimitNumberAction,
    setSortData as setSortDataAction,
    setFilterData as setFilterDataAction,
    setError as setErrorAction,
    fetchReportColumns,
    fetchReportResults,
    exportReport,
    calcReport,
    saveReportColumns,
} from "../../../reducers/reports-modeling/roleLevelSlice";

import { selectModelingRolesContainsGroupRoles } from "../../../reducers/reports-modeling/roleLevelSlice";

import useReportReduxTable from "../../common/hooks/useReportReduxTable";

import ReportResults from "../../sod-reports/report-results";
import ConflictLevelsPicker from "../conflict-levels-picker";
import useReduxValue from "../../common/hooks/useReduxValue";
import { REPORT_MODELING_CONFLICT_LEVELS } from "../../../utils/reports-modeling-utils.ts";
import { reportsModelingService } from "../../../services/reports-modeling-service.ts";

const RoleLevelModelingResults = () => {
    const dispatch = useDispatch();

    const {
        rows,
        columns,
        total,
        busy,
        selectedIds,
        searchString,
        setSearchString,
        pageNumber,
        setPageNumber,
        limitNumber,
        setLimitNumber,
        filterData,
        setFilterData,
        sortData,
        setSortData,
        reportLevel,
        setReportLevel,
        reportType,
        setReportType,
        open,
        setOpen,
    } = useReportReduxTable(
        (state) => state.reportsModeling.roleLevel.report.rows,
        fetchReportResults,
        (state) => state.reportsModeling.roleLevel.report.columns,
        fetchReportColumns,
        (state) => state.reportsModeling.roleLevel.report.selectedIds,
        setSelectedIdsAction,
        (state) => state.reportsModeling.roleLevel.report.searchString,
        setSearchStringAction,
        (state) => state.reportsModeling.roleLevel.report.pageNumber,
        setPageNumberAction,
        (state) => state.reportsModeling.roleLevel.report.limitNumber,
        setLimitNumberAction,
        (state) => state.reportsModeling.roleLevel.report.sortData,
        setSortDataAction,
        (state) => state.reportsModeling.roleLevel.report.filterData,
        setFilterDataAction,
        (state) => state.reportsModeling.roleLevel.error,
        setErrorAction,
        (state) => state.reportsModeling.roleLevel.report.total,
        (state) => state.reportsModeling.roleLevel.busy,
        (state) => state.reportsModeling.roleLevel.report.reportLevel,
        setReportLevelWithColumns,
        (state) => state.reportsModeling.roleLevel.report.reportType,
        setReportTypeWithColumns,
        (state) => state.reportsModeling.roleLevel.report.resultsOpen,
        setResultsOpenAction
    );

    const [conflictLevel] = useReduxValue((state) => state.reportsModeling.roleLevel.report.conflictLevel, setConflictLevelWithColumns);

    const getColumns = (reportLevel, reportType, defaultColumns) => {
        return conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME
            ? reportsModelingService.getUserLevelModelingColumns(reportLevel, reportType, defaultColumns)
            : reportsModelingService.getRoleLevelModelingColumns(reportLevel, reportType, defaultColumns);
    };

    const handleExportClick = () => {
        dispatch(exportReport());
    };

    const handleSaveColumns = (columns, reportLevel, reportType, conflictLevel) => {
        dispatch(
            saveReportColumns({
                columns,
                reportLevel,
                reportType,
                conflictLevel,
            })
        );
    };

    const isComplex = useSelector(selectModelingRolesContainsGroupRoles);

    const conflictLevels = useMemo(() => {
        if (isComplex) {
            return Object.values({
                ROLE: "role",
                USERNAME: "username",
            });
        }

        return Object.values(REPORT_MODELING_CONFLICT_LEVELS);
    }, [isComplex]);

    const changeConflictLevel = async (conflictLevel) => {
        await dispatch(calcReport({ conflictLevel: conflictLevel, calcWithReset: false })).unwrap();
        await dispatch(fetchReportColumns());
        // await setConflictLevel(conflictLevel)
    };

    return (
        <ReportResults
            open={open}
            setOpen={setOpen}
            reportLevel={reportLevel}
            setReportLevel={setReportLevel}
            reportType={reportType}
            setReportType={setReportType}
            rows={rows}
            columns={columns}
            total={total}
            busy={busy}
            selectedIds={selectedIds}
            searchString={searchString}
            setSearchString={setSearchString}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            limitNumber={limitNumber}
            setLimitNumber={setLimitNumber}
            filterData={filterData}
            setFilterData={setFilterData}
            sortData={sortData}
            setSortData={setSortData}
            onExportClick={handleExportClick}
            filterId="role-report"
            collapseChildren={<ConflictLevelsPicker value={conflictLevel} conflictLevels={conflictLevels} onChange={changeConflictLevel} />}
            conflictLevel={conflictLevel}
            getColumns={getColumns}
            saveReportColumns={handleSaveColumns}
        />
    );
};

export default RoleLevelModelingResults;
